import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./BlogList.css";
import {
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaCalendarAlt,
  FaUser,
  FaAngleLeft,
  FaAngleRight,
  FaEye,
  FaEyeSlash,
  FaTags,
} from "react-icons/fa";
import { Typography, CardContent, Card, Box } from "@mui/material";
import { BiCategory } from "react-icons/bi";
import Header2 from "./Header2/Header2";
import PageTitle from "./pagetitle/PageTitle";
import Footer from "./footer/Footer";
import SubscriptionForm from "./SubscriptionForm";
import hrImage from "../images/hr-image.jpg";

const BlogList = ({ isAdmin = false }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [availableTags, setAvailableTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [showAllTags, setShowAllTags] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    author: "",
    startDate: "",
    endDate: "",
    sort: "newest",
    status: isAdmin ? "all" : "active", // Default to show only active posts for non-admins
  });
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [activeCategoryItem, setActiveCategoryItem] = useState("");
  // Changed to 9 blogs per page
  const itemsPerPage = 9;

  // Categories with icons
  const categories = [
    { value: "Unified events", label: "Unified events", icon: <BiCategory /> },
    { value: "HR & Job", label: "HR & Job", icon: <BiCategory /> },
    { value: "IT Technology", label: "IT Technology", icon: <BiCategory /> },
    {
      value: "AI & Automation",
      label: "AI & Automation",
      icon: <BiCategory />,
    },
    { value: "BFSI", label: "BFSI", icon: <BiCategory /> },
    { value: "Education", label: "Education", icon: <BiCategory /> },
    { value: "Cyber Security", label: "Cyber Security", icon: <BiCategory /> },
    {
      value: "Contact Center ",
      label: "Contact Center ",
      icon: <BiCategory />,
    },
    {
      value: "Sales Marketing",
      label: "Sales Marketing",
      icon: <BiCategory />,
    },
    {
      value: "Digital Marketing",
      label: "Digital Marketing",
      icon: <BiCategory />,
    },
    { value: "Others", label: " Others", icon: <BiCategory /> },
  ];

  // Sort options
  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "title-asc", label: "Title (A-Z)" },
    { value: "title-desc", label: "Title (Z-A)" },
  ];

  // Status options (only visible to admins)
  const statusOptions = [
    { value: "all", label: "All Posts", icon: <FaEye /> },
    { value: "active", label: "Published Only", icon: <FaEye /> },
    { value: "inactive", label: "Drafts Only", icon: <FaEyeSlash /> },
  ];

  const visibleCategories = showAllCategories
    ? categories
    : categories.slice(0, 2);

  const visibleTags = showAllTags ? availableTags : availableTags.slice(0, 5);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handleCategoryChange = (category, e) => {
    e.preventDefault();
    if (filters.category === category) {
      setFilters((prev) => ({ ...prev, category: "" }));
      setActiveCategoryItem("");
    } else {
      setFilters((prev) => ({ ...prev, category }));
      setActiveCategoryItem(category);
    }
    setPage(1);
  };

  const handleTagFilter = (tag) => {
    if (activeTag === tag) {
      setActiveTag("");
      setSearchTerm("");
    } else {
      setActiveTag(tag);
      setSearchTerm(tag);
    }
    setPage(1);
  };

  const handleAuthorChange = (e) => {
    setFilters((prev) => ({ ...prev, author: e.target.value }));
    setPage(1);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleSortChange = (sortOption) => {
    setFilters((prev) => ({ ...prev, sort: sortOption }));
    setShowSortDropdown(false);
    setPage(1);
  };

  const handleStatusChange = (status) => {
    setFilters((prev) => ({ ...prev, status }));
    setShowStatusDropdown(false);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilters({
      category: "",
      author: "",
      startDate: "",
      endDate: "",
      sort: "newest",
      status: isAdmin ? "all" : "active", // Reset to default based on user role
    });
    setActiveCategoryItem("");
    setActiveTag("");
    setPage(1);
  };

  const isFilterActive = () => {
    return (
      filters.category !== "" ||
      filters.author !== "" ||
      filters.startDate !== "" ||
      filters.endDate !== "" ||
      searchTerm !== "" ||
      filters.sort !== "newest" ||
      (isAdmin && filters.status !== "all") ||
      activeTag !== ""
    );
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError("");

        const params = {
          page,
          limit: itemsPerPage,
          ...(searchTerm && { search: searchTerm }),
          ...(filters.category && { category: filters.category }),
          ...(filters.author && { author: filters.author }),
          ...(filters.startDate && { startDate: filters.startDate }),
          ...(filters.endDate && { endDate: filters.endDate }),
          ...(filters.sort && { sort: filters.sort }),
          // Add status filter
          ...(filters.status !== "all" && {
            isActive: filters.status === "active" ? true : false,
          }),
        };

        // FIX: Add authorization headers
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog`,
          {
            params,
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json'
            }
          }
        );
        setBlogs(response.data.data);
        setTotalPages(response.data.totalPages);

        if (availableTags.length === 0 && response.data.data.length > 0) {
          const allTags = [];
          response.data.data.forEach((blog) => {
            if (blog.keywords) {
              if (Array.isArray(blog.keywords)) {
                allTags.push(...blog.keywords);
              } else if (typeof blog.keywords === "string") {
                const keywordsArray = blog.keywords
                  .split(",")
                  .map((tag) => tag.trim());
                allTags.push(...keywordsArray);
              }
            }
          });

          const uniqueTags = [...new Set(allTags)].filter(
            (tag) => tag && tag.trim() !== ""
          );
          setAvailableTags(uniqueTags);
        }

        if (authors.length === 0 && response.data.data.length > 0) {
          const uniqueAuthors = [
            ...new Set(response.data.data.map((blog) => blog.authorName)),
          ];
          setAuthors(uniqueAuthors);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setError("Failed to load blogs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [page, searchTerm, filters]);

  const PaginationControls = () => {
    // Always show pagination controls
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => setPage(i)}
          className={`page-number ${page === i ? "active" : ""}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination-container">
        <button
          onClick={() => setPage(1)}
          disabled={page === 1}
          className="pagination-button"
        >
          First
        </button>

        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="pagination-button"
        >
          <FaAngleLeft /> Prev.
        </button>

        <div className="page-numbers">
          {startPage > 1 && <span className="page-ellipsis">...</span>}
          {pageNumbers}
          {endPage < totalPages && <span className="page-ellipsis">...</span>}
        </div>

        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className="pagination-button"
        >
          Next <FaAngleRight />
        </button>

        <button
          onClick={() => setPage(totalPages)}
          disabled={page === totalPages}
          className="pagination-button"
        >
          Last
        </button>
      </div>
    );
  };

  const renderTags = (keywords) => {
    if (!keywords) return null;

    let tags = [];
    if (Array.isArray(keywords)) {
      tags = keywords;
    } else if (typeof keywords === "string") {
      tags = keywords.split(",").map((tag) => tag.trim());
    }

    return tags
      .filter((tag) => tag && tag.trim() !== "")
      .slice(0, 2)
      .map((tag, index) => (
        <span key={index} className="blog-tag">
          #{tag}
        </span>
      ));
  };

  return (
    <>
      <Header2 />
      <PageTitle pageTitle={"Insights & Articles"} />

      <div className="blog-page-layout">
        <div className="blog-content-wrapper flex flex-col md:flex-row">
          <div className="blog-sidebar p-4 border order-2 md:order-1">
            <div className="flex">
              <h4 className="filter-title">
                {/* <BiCategory className="filter-icon" />  */}
                Categories
              </h4>
              <ul className="category-list">
                {visibleCategories.map((cat) => (
                  <li
                    key={cat.value}
                    className={`category-item ${
                      activeCategoryItem === cat.value ? "active" : ""
                    }`}
                    onClick={(e) => handleCategoryChange(cat.value, e)}
                  >
                    {/* <span className="category-icon">{cat.icon}</span> */}
                    {cat.label}
                  </li>
                ))}

                {!showAllCategories && categories.length > 2 && (
                  <li
                    className="view-all-categories"
                    onClick={() => setShowAllCategories(true)}
                  >
                    View All Categories →
                  </li>
                )}
              </ul>
            </div>

            {/* MODIFIED: Tags Filter Section with box format */}
            <div className="flex py-4">
              <h4 className="filter-title">
                {/* <FaTags className="filter-icon" />  */}
                Tags
              </h4>
              <div className="tags-box-container">
                {visibleTags.map((tag) => (
                  <span
                    key={tag}
                    className={`tag-box ${activeTag === tag ? "active" : ""}`}
                    onClick={() => handleTagFilter(tag)}
                  >
                    {tag}
                  </span>
                ))}

                {!showAllTags && availableTags.length > 5 && (
                  <span
                    className="view-all-tags flex"
                    onClick={() => setShowAllTags(true)}
                  >
                    View All Tags →
                  </span>
                )}
              </div>
            </div>

            <div className="flex py-4">
              <h4 className="filter-title">
                {/* <FaUser className="filter-icon" />  */}
                Select Author
              </h4>
              <select
                value={filters.author}
                onChange={handleAuthorChange}
                className="filter-dropdown"
              >
                <option value="">All Authors</option>
                {authors.map((author) => (
                  <option key={author} value={author}>
                    {author}
                  </option>
                ))}
              </select>
            </div>

            {/* Status filter section for admin users */}
            {isAdmin && (
              <div className="filter-section">
                <h4 className="filter-title">
                  <FaEye className="filter-icon" /> Status
                </h4>
                <ul className="status-list">
                  {statusOptions.map((status) => (
                    <li
                      key={status.value}
                      className={`status-item ${
                        filters.status === status.value ? "active" : ""
                      }`}
                      onClick={() => handleStatusChange(status.value)}
                    >
                      <span className="status-icon">{status.icon}</span>
                      {status.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isFilterActive() && (
              <button className="clear-filters-btn" onClick={clearFilters}>
                Clear All Filters
              </button>
            )}

            

            <div className="sidebar-extra">
              <div className="ad-placeholder">
                <Card
                  variant="outlined"
                  sx={{
                    maxWidth: "345px",
                    position: "relative",
                    border: "none",
                    display: "block",
                    background: "linear-gradient(45deg, #0045ff, #00a9c6)",
                    backgroundSize: "400% 400%",
                    // animation: `${gradientAnimation} 1s ease infinite`,
                    zIndex: "999",
                    textAlign: "left",
                    color: "#fff",
                  }}
                >
                  <CardContent sx={{ padding: "0px !important" }}>
                    <img src={hrImage} alt="UFS Network jobs" />
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: "16px",
                        color: "#fff",
                        padding: "10px",
                      }}
                    >
                      Exciting Job Opportunity with Our Client!
                    </Typography>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        color: "#fff",
                        fontSize: "12px",
                        padding: "0px 10px",
                      }}
                    >
                      We're hiring on behalf of a reputed organization looking
                      for talented professionals to join their team.
                      <br />
                    </Typography>
                    <Box sx={{ padding: "10px" }}>
                      <Typography
                        component={Link}
                        to="/career"
                        sx={{
                          width: "100%",
                          fontSize: "14px",
                          textAlign: "center",
                          color: "#000",
                          marginTop: "4px",
                          backgroundColor: "#fff",
                          display: "block",
                          padding: "12px 10px",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        View jobs
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </div>
            </div>
            {/* Import the SubscriptionForm component */}
            <SubscriptionForm />
          </div>

          <div className="blog-main-content order-1 md:order-1">
            {/* Blog search section start from here */}
            <div className="blog-header">
              <div className="header-controls">
                <form onSubmit={handleSearch} className="search-form">
                  <div className="search-input-container">
                    <FaSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search blogs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  <button type="submit" className="search-button">
                    <i className="fa fa-search me-1"></i> Search
                  </button>
                </form>

                <div className="sort-dropdown">
                  <button
                    className="sort-button"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                  >
                    <FaFilter />
                    {/* Sort{" "}
                    {showSortDropdown ? <FaChevronUp /> : <FaChevronDown />} */}
                  </button>
                  {showSortDropdown && (
                    <div className="sort-dropdown-content">
                      {sortOptions.map((option) => (
                        <div
                          key={option.value}
                          className={`sort-option ${
                            filters.sort === option.value ? "active" : ""
                          }`}
                          onClick={() => handleSortChange(option.value)}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Status filter dropdown - only visible to admins */}
                {isAdmin && (
                  <div className="sort-dropdown">
                    <button
                      className="sort-button"
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    >
                      {filters.status === "active" ? (
                        <FaEye />
                      ) : filters.status === "inactive" ? (
                        <FaEyeSlash />
                      ) : (
                        <FaEye />
                      )}{" "}
                      Status{" "}
                      {showStatusDropdown ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    {showStatusDropdown && (
                      <div className="sort-dropdown-content">
                        {statusOptions.map((option) => (
                          <div
                            key={option.value}
                            className={`sort-option ${
                              filters.status === option.value ? "active" : ""
                            }`}
                            onClick={() => handleStatusChange(option.value)}
                          >
                            {option.icon} {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Blog list start from here */}
            {isFilterActive() && (
              <div className="active-filters">
                <span className="active-filters-title">Active Filters:</span>

                {filters.category && (
                  <span className="filter-tag">
                    <span className="fw-600">Category:</span>{" "}
                    {categories.find((c) => c.value === filters.category)
                      ?.label || filters.category}
                    <button
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, category: "" }));
                        setActiveCategoryItem("");
                      }}
                      className="remove-tag"
                    ></button>
                  </span>
                )}

                {/* Show active tag filter */}
                {activeTag && (
                  <span className="filter-tag">
                    <span className="fw-600">Tag:</span> {activeTag}
                    <button
                      onClick={() => {
                        setActiveTag("");
                        setSearchTerm("");
                      }}
                      className="remove-tag"
                    ></button>
                  </span>
                )}

                {filters.author && (
                  <span className="filter-tag">
                    <span className="fw-600">Author:</span> {filters.author}
                    <button
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, author: "" }))
                      }
                      className="remove-tag"
                    ></button>
                  </span>
                )}

                {searchTerm && !activeTag && (
                  <span className="filter-tag">
                    Search: "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm("")}
                      className="remove-tag"
                    ></button>
                  </span>
                )}

                {filters.sort && filters.sort !== "newest" && (
                  <span className="filter-tag">
                    Sort:{" "}
                    {sortOptions.find((s) => s.value === filters.sort)?.label}
                    <button
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, sort: "newest" }))
                      }
                      className="remove-tag"
                    ></button>
                  </span>
                )}

                {/* Display status filter tag for admin users */}
                {isAdmin && filters.status !== "all" && (
                  <span className="filter-tag">
                    Status:{" "}
                    {
                      statusOptions.find((s) => s.value === filters.status)
                        ?.label
                    }
                    <button
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, status: "all" }))
                      }
                      className="remove-tag"
                    ></button>
                  </span>
                )}
              </div>
            )}

            {loading ? (
              <div className="loading-spinner">Loading...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : blogs.length === 0 ? (
              <div className="no-results">
                <p>
                  No blog posts found. Try adjusting your search or filters.
                </p>
                {isFilterActive() && (
                  <button className="reset-search-btn" onClick={clearFilters}>
                    Reset All Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="blog-grid three-column">
                  {blogs.map((blog) => (
                    <article key={blog._id} className="blog-card">
                      <Link
                        to={`/blog/${blog.slug}`}
                        className="blog-card-link"
                      >
                        <div className="blog-image-container">
                          {blog.image ? (
                            <img
                              src={blog.image}
                              alt={blog.title}
                              className="blog-image"
                              loading="lazy"
                            />
                          ) : (
                            <div className="blog-image-placeholder">
                              <span>{blog.title.charAt(0)}</span>
                            </div>
                          )}
                          {blog.category && (
                            <span className="category-tag">
                              {categories.find((c) => c.value === blog.category)
                                ?.label || blog.category}
                            </span>
                          )}

                          {/* Show keywords as tags */}
                          {blog.keywords && (
                            <div className="blog-tags">
                              {renderTags(blog.keywords)}
                            </div>
                          )}

                          {/* Display status badge for admin users */}
                          {isAdmin && (
                            <span
                              className={`status-badge ${
                                blog.isActive ? "active" : "inactive"
                              }`}
                            >
                              {blog.isActive ? "Published" : "Draft"}
                            </span>
                          )}
                        </div>
                        <div className="blog-content">
                          <h2 className="blog-title">{blog.title}</h2>
                          <div
                            className="flex"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "16px",
                               flexDirection:"column",
                            }}
                          >
                            <p className="blog-meta">
                              <span className="me-2">
                                <i className="fa-regular fa-user me-1"></i>
                                {blog.authorName}
                              </span>
                              <span>
                                <i className="fa fa-calendar-days me-1"></i>
                                {new Date(blog.createdAt).toLocaleDateString()}
                              </span>
                            </p>
                            <span className="read-more-text text-end">Read More →</span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>

                {/* Show pagination always, not just when totalPages > 1 */}
                <PaginationControls />
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogList;
